import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Video, 
  Mail, 
  MessageSquare, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats
      const statsResponse = await axios.get(`${API}/api/admin/stats`, { headers });
      
      // Fetch recent enquiries
      const enquiriesResponse = await axios.get(`${API}/api/admin/enquiries`, { headers });
      
      // Transform stats for display
      const statsData = [
        {
          label: 'Total Enquiries',
          value: statsResponse.data.total_enquiries.toString(),
          change: `+${statsResponse.data.pending_enquiries} pending`,
          icon: Mail,
          color: 'from-cyan-500 to-blue-500'
        },
        {
          label: 'Sample Videos',
          value: statsResponse.data.total_videos.toString(),
          change: 'Active',
          icon: Video,
          color: 'from-amber-500 to-amber-600'
        },
        {
          label: 'Pending Orders',
          value: statsResponse.data.pending_enquiries.toString(),
          change: 'Need attention',
          icon: MessageSquare,
          color: 'from-purple-500 to-pink-500'
        },
        {
          label: 'Conversion Rate',
          value: `${statsResponse.data.conversion_rate}%`,
          change: '+5%',
          icon: TrendingUp,
          color: 'from-green-500 to-teal-500'
        }
      ];

      setStats(statsData);
      
      // Get last 3 enquiries
      const recent = enquiriesResponse.data.enquiries
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 3)
        .map(enq => ({
          id: enq.id,
          name: enq.name,
          type: enq.slideshowType || enq.packageName || 'Unknown',
          date: formatDate(enq.timestamp),
          status: enq.status || 'pending',
          photoCount: enq.photoCount || 0
        }));
      
      setRecentEnquiries(recent);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Failed to load dashboard data');
      }
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      contacted: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      completed: 'bg-green-500/20 text-green-400 border-green-500/50'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-8">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-gray-400">Welcome back! Here's what's happening with Visual Creationz.</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats && stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-cyan-500 transition-all duration-300 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon size={24} className="text-white" />
                    </div>
                    <span className="text-green-400 text-sm font-medium">{stat.change}</span>
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Recent Enquiries */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Recent Enquiries</h2>
                <a href="/admin/enquiries" className="text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors">
                  View All →
                </a>
              </div>
            </div>
            
            <div className="divide-y divide-gray-700">
              {recentEnquiries.length > 0 ? (
                recentEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="p-6 hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-white font-semibold mb-1">{enquiry.name}</h3>
                        <div className="flex items-center space-x-3 text-sm text-gray-400">
                          <span className="flex items-center">
                            <Clock size={14} className="mr-1" />
                            {enquiry.date}
                          </span>
                          <span>•</span>
                          <span>{enquiry.type}</span>
                          {enquiry.photoCount > 0 && (
                            <>
                              <span>•</span>
                              <span>{enquiry.photoCount} photos</span>
                            </>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(enquiry.status)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-gray-400">
                  No enquiries yet. They'll appear here once customers start ordering!
                </div>
              )}
            </div>
          </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <a
          href="/admin/videos"
          className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-amber-500 transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Video size={24} className="text-black" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Manage Videos</h3>
              <p className="text-gray-400 text-sm">Add or edit samples</p>
            </div>
          </div>
        </a>

        <a
          href="/admin/enquiries"
          className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-cyan-500 transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Mail size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">View Enquiries</h3>
              <p className="text-gray-400 text-sm">
                {stats && stats[0] ? stats[0].value : '0'} total orders
              </p>
            </div>
          </div>
        </a>

        <a
          href="/admin/settings"
          className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-purple-500 transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <MessageSquare size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Settings</h3>
              <p className="text-gray-400 text-sm">Update preferences</p>
            </div>
          </div>
        </a>
      </div>

          {/* Quick Tips */}
          <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 rounded-2xl p-6 border border-cyan-500/30">
            <div className="flex items-start space-x-4">
              <AlertCircle size={24} className="text-cyan-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-semibold mb-2">Quick Tip</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Respond to enquiries within 24 hours to increase conversion rates. Consider setting up email notifications to never miss a potential client!
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
